import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import styled from "styled-components";
import { useGetArticlesQuery, Article } from "../../services/rtkApi/agentApi";
import { useIsWriter } from "./useIsWriter";

// mirrors ARTICLE_CATEGORIES in n450s_backend/models/agent/article.ts —
// the first five are the sitebot's original set, the rest are for human posts
const CATEGORIES = [
  "all",
  "gossip",
  "review",
  "spotlight",
  "editorial",
  "digest",
  "notes",
  "guide",
  "announcement",
] as const;

const BlogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";
  const { isWriter: canWrite } = useIsWriter();

  const queryArgs = activeCategory === "all"
    ? { limit: 50 }
    : { limit: 50, category: activeCategory };

  const { data, isLoading, isError } = useGetArticlesQuery(queryArgs);

  const handleCategoryClick = (cat: string) => {
    if (cat === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <Title>blog</Title>
        <Subtitle>dispatches from n450s and its agent</Subtitle>
        <HeaderNav>
          <NavLink to="/blog/books">books →</NavLink>
        </HeaderNav>
        {canWrite && (
          <WriterBar>
            <WriterLink to="/blog/new">+ write a post</WriterLink>
            <WriterLink to="/blog/manage" $secondary>
              manage posts
            </WriterLink>
            <WriterLink to="/blog/manage/books" $secondary>
              manage books
            </WriterLink>
          </WriterBar>
        )}
      </PageHeader>

      <CategoryBar>
        {CATEGORIES.map((cat) => (
          <CategoryPill
            key={cat}
            $active={activeCategory === cat}
            onClick={() => handleCategoryClick(cat)}
          >
            {cat}
          </CategoryPill>
        ))}
      </CategoryBar>

      <ArticlesGrid>
        {isLoading && <LoadingText>loading articles...</LoadingText>}
        {isError && <LoadingText>failed to load articles</LoadingText>}
        {data?.articles.length === 0 && !isLoading && (
          <EmptyState>no articles yet. check back soon.</EmptyState>
        )}
        {data?.articles.map((article) => (
          <ArticleCard key={article._id} article={article} />
        ))}
      </ArticlesGrid>
    </PageContainer>
  );
};

const ArticleCard: React.FC<{ article: Article }> = ({ article }) => {
  const date = new Date(article.publishedAt);
  const formattedDate = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <CardLink to={`/blog/${article.slug}`}>
      <Card>
        <CardMeta>
          <CategoryTag>{article.category}</CategoryTag>
          <DateText>{formattedDate}</DateText>
          {article.author && <AuthorText>{article.author.label}</AuthorText>}
        </CardMeta>
        <CardTitle>{article.title}</CardTitle>
        {article.subtitle && <CardSubtitle>{article.subtitle}</CardSubtitle>}
        {article.tags.length > 0 && (
          <TagList>
            {article.tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </TagList>
        )}
      </Card>
    </CardLink>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f0f0;
  padding: 20px;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
  padding: 20px 0;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  color: #222;
  text-transform: uppercase;
  margin: 0;
  text-shadow: 2px 2px 5px #3c3c3cae;

  @media (max-width: 700px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #666;
  margin: 8px 0 0;
  font-style: italic;
`;

const HeaderNav = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
`;

const NavLink = styled(Link)`
  color: #466a5d;
  font-size: 0.9rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const WriterBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const WriterLink = styled(Link)<{ $secondary?: boolean }>`
  padding: 8px 18px;
  font-size: 0.88rem;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid ${(p) => (p.$secondary ? "#555" : "#466a5d")};
  background: ${(p) => (p.$secondary ? "white" : "#9beece")};
  color: #222;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.4);

  &:hover {
    box-shadow: 3px 4px rgba(0, 0, 0, 0.45);
  }

  &:active {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.4);
  }
`;

const CategoryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 28px;
`;

const CategoryPill = styled.button<{ $active: boolean }>`
  padding: 8px 18px;
  border: 1px solid ${(p) => (p.$active ? "#466a5d" : "#ccc")};
  background: ${(p) => (p.$active ? "#9beece" : "white")};
  color: ${(p) => (p.$active ? "#222" : "#555")};
  font-weight: ${(p) => (p.$active ? 700 : 400)};
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: ${(p) =>
    p.$active
      ? "2px 3px rgba(0, 0, 0, 0.6)"
      : "1px 2px rgba(0, 0, 0, 0.2)"};
  transition: all 0.15s;

  &:hover {
    background: ${(p) => (p.$active ? "#9beece" : "#d4f9eb")};
    box-shadow: 2px 3px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.4);
  }
`;

const ArticlesGrid = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const Card = styled.div`
  background: white;
  border: 1px solid #ccc;
  padding: 20px 24px;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.15);
  transition: all 0.15s;

  &:hover {
    box-shadow: 3px 5px rgba(0, 0, 0, 0.25);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 1px 2px rgba(0, 0, 0, 0.2);
  }
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const CategoryTag = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #466a5d;
  background: #d4f9eb;
  padding: 2px 8px;
  border: 1px solid #466a5d;
`;

const DateText = styled.span`
  font-size: 0.8rem;
  color: #888;
`;

const AuthorText = styled.span`
  font-size: 0.78rem;
  color: #999;
  font-style: italic;
`;

const CardTitle = styled.h2`
  font-size: 1.4rem;
  color: #222;
  margin: 0 0 4px;
  line-height: 1.3;
`;

const CardSubtitle = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 0 0 8px;
  line-height: 1.4;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

const Tag = styled.span`
  font-size: 0.7rem;
  color: #777;
  background: #f5f5f5;
  padding: 2px 8px;
  border: 1px solid #ddd;
`;

const LoadingText = styled.p`
  text-align: center;
  color: #888;
  font-size: 1rem;
  padding: 40px 0;
`;

const EmptyState = styled.p`
  text-align: center;
  color: #888;
  font-size: 1.1rem;
  padding: 60px 0;
  font-style: italic;
`;

export default BlogPage;
